/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Type-only: the value types of the catalog fields are derived from the one
// vocabulary (src/lib/chargerCatalogEnums.js), so a value added there is a
// value here, and nothing of that module enters this one at runtime.
import type {
  CHARGER_AUDIENCES,
  CHARGER_AVAILABILITY,
  CHARGER_CONNECTIVITY,
  CHARGER_CONNECTORS,
  CHARGER_LOAD_BALANCING,
  CHARGER_OCPP_VERSIONS,
  CHARGER_PHASES,
} from './chargerCatalogEnums.js';

export type MidStatus = 'yes' | 'no' | 'depends';

// Smart = remotely readable / OCPP-connected. Required by some ERE providers
// alongside MID so they can pull session data automatically (no manual upload).
export type SmartStatus = 'yes' | 'no' | 'depends';

// How the MID and non-MID versions of one product are told apart. Powers the
// "which one do I own?" guidance and lets the checker group siblings that share
// a `family`. On a standalone row it explains *why* the answer is conditional.
//   date       — a production/sales cut-off date (Tesla Gen 3 from Sep 2025)
//   sku        — a separate article/SKU or model suffix ("…MID", "-M", "Pro")
//   generation — a hardware generation swap under the same name (Zaptec Go 2)
//   retrofit   — MID only if an add-on/retrofit kit is fitted (Hager Witty XEV)
export type VariantKind = 'date' | 'sku' | 'generation' | 'retrofit';

// --- The laadpalen catalog (2026-10-03) -----------------------------------
// Research per model and per manufacturer. Every rule — resolution, payloads,
// sanitizers, slugs — lives in src/lib/chargerCatalogRecords.js and
// src/lib/chargerCatalog.js; these are their shapes.

export type ChargerPhases = (typeof CHARGER_PHASES)[number];
export type ChargerConnector = (typeof CHARGER_CONNECTORS)[number];
export type ChargerConnectivity = (typeof CHARGER_CONNECTIVITY)[number];
export type ChargerOcppVersion = (typeof CHARGER_OCPP_VERSIONS)[number];
export type ChargerLoadBalancing = (typeof CHARGER_LOAD_BALANCING)[number];
export type ChargerAudience = (typeof CHARGER_AUDIENCES)[number];
export type ChargerAvailability = (typeof CHARGER_AVAILABILITY)[number];

/**
 * A model's technical specs, as its manufacturer states them. An absent field
 * is unknown. `connectivity: []` and `ocpp: []` are claims ("none"), not
 * unknowns.
 */
export interface ChargerSpecs {
  /** Maximum AC charging power of this row's product, kW. */
  powerKw?: number;
  phases?: ChargerPhases;
  connector?: ChargerConnector;
  connectivity?: ChargerConnectivity[];
  ocpp?: ChargerOcppVersion[];
  loadBalancing?: ChargerLoadBalancing;
  /** A solar-surplus charging mode. */
  solar?: boolean;
  /** Card/tag authorisation. */
  rfid?: boolean;
  display?: boolean;
}

/** Where a model stands in its manufacturer's range. */
export interface ChargerContext {
  audience?: ChargerAudience[];
  launchYear?: number;
  availability?: ChargerAvailability;
  /** The model that replaced it, as the manufacturer calls it. */
  successor?: string;
}

/** A cited document; `title` may be empty (show the host). */
export interface ChargerSource {
  url: string;
  title: string;
}

export interface ChargerLangText {
  nl?: string;
  en?: string;
}

export interface ChargerModel {
  brand: string;
  model: string;
  status: MidStatus;
  smart: SmartStatus;
  note?: ChargerLangText;
  // Optional classification (added 2026-07, purely additive — the stored
  // `brand::model` key is unaffected). `family` is a stable slug shared by every
  // row that is the same physical product split into MID/non-MID variants, so
  // the checker can link and group siblings. `variantKind` says *how* those
  // variants differ. Both absent = a plain standalone product.
  family?: string;
  variantKind?: VariantKind;
  // The catalog fields (2026-10-03, additive like the two above). Research
  // ships them in src/lib/chargerCatalogSeed.js; an admin edit overrides only
  // the leaves it changed. The public register (GET /api/mid-chargers) carries
  // `specs` only; the rest is for the brand pages.
  specs?: ChargerSpecs;
  context?: ChargerContext;
  /** Product picture, site-relative (/images/… or /uploads/…). */
  image?: string;
  sources?: ChargerSource[];
  /** The day the research was checked, YYYY-MM-DD. Not the verdict's date. */
  checkedAt?: string;
}

/** One entry of CHARGER_DETAILS_SEED — what research adds to a register row. */
export type ChargerDetails = Pick<
  ChargerModel,
  'specs' | 'context' | 'image' | 'sources' | 'checkedAt' | 'note'
>;

/**
 * The register as GET /api/mid-chargers serves it and the bake seeds it on
 * /mid-check. `brandPages` (brand name → slug of its published page) rides on
 * the public payload only; server-side reads of the register leave it out.
 */
export interface MidChargersData {
  models: ChargerModel[];
  updated_at: string;
  brandPages?: Record<string, string>;
}

/**
 * A manufacturer: one per distinct `brand` of the effective register.
 * `seoTitle`, `seoDescription` and `published` are admin-only; an effective
 * record (resolveChargerBrands) always has `published`.
 */
export interface ChargerBrand {
  slug: string;
  brand: string;
  company?: string;
  /** ISO 3166-1 alpha-2 of the head office. */
  country?: string;
  city?: string;
  founded?: number;
  parent?: string;
  website?: string;
  description?: ChargerLangText;
  support?: { url?: string; phone?: string; email?: string };
  app?: { name: string };
  /** How charging data gets out: own portal/cloud, OCPP to a third party. */
  backoffice?: ChargerLangText;
  warranty?: { years?: number; note?: ChargerLangText };
  /** Logo, site-relative. */
  logo?: string;
  sources?: ChargerSource[];
  checkedAt?: string;
  seoTitle?: ChargerLangText;
  seoDescription?: ChargerLangText;
  published?: boolean;
}

/** A manufacturer record as a visitor gets it — chargerCatalogRecords.js publicChargerBrandOf. */
export type ChargerBrandPublic = Omit<ChargerBrand, 'published'>;

/**
 * What the hub reads of one model — chargerCatalogRecords.js hubModelFactsOf:
 * the brand filters its verdicts and phases, the model finder (2026-10-21)
 * the rest. Keys in this order; every optional fact absent = unknown.
 */
export interface ChargerBrandIndexModel {
  /** The register's name of the model. */
  model: string;
  /** Its anchor on its brand page — the brand page's own, as given. */
  anchor: string;
  status: MidStatus;
  smart: SmartStatus;
  powerKw?: number;
  phases?: ChargerPhases;
  connector?: ChargerConnector;
  solar?: boolean;
  /** Who the manufacturer makes it for (`context.audience`), in the vocabulary's order. */
  audience?: ChargerAudience[];
}

/** A row of the brand hub — chargerCatalogRecords.js chargerBrandIndexRows. */
export interface ChargerBrandIndexRow {
  slug: string;
  brand: string;
  logo: string | null;
  country: string | null;
  modelCount: number;
  mid: { yes: number; depends: number; no: number };
  /** The brand's register rows in register order, as the hub's filters and finder read them. */
  models: ChargerBrandIndexModel[];
}

/** A model on its brand page — chargerCatalogRecords.js publicChargerModelOf. */
export interface ChargerPublicModel extends ChargerModel {
  /**
   * `#anchor` of the model on its brand page — unique on that page and never
   * empty in a chargerBrandDetailPayload; the page and its JSON-LD use it as given.
   */
  anchor: string;
}

/** GET /api/charger-brands/:slug — chargerCatalogRecords.js chargerBrandDetailPayload. */
export interface ChargerBrandDetail {
  brand: ChargerBrandPublic;
  models: ChargerPublicModel[];
  counts: { total: number; yes: number; depends: number; no: number };
  previous: { slug: string; brand: string } | null;
  next: { slug: string; brand: string } | null;
}

/** One item of a model's facts line — chargerCatalog.js chargerFactTokens. */
export type ChargerFactToken =
  | { kind: 'power'; kw: number }
  | { kind: 'phases'; value: ChargerPhases }
  | { kind: 'connector'; value: ChargerConnector }
  | { kind: 'connectivity'; values: ChargerConnectivity[] };

// The seed itself lives in midChargersSeed.ts (its own module so the client
// bundle can leave it out); re-exported here so every existing server, SSR,
// script and test import keeps working unchanged.
export { MID_CHARGERS_SEED } from './midChargersSeed.js';

// When the seed was last reconciled against the registrar lists. Lives here,
// not next to the array: a page that only wants the date must not link the
// seed module into the browser bundle.
export const MID_CHARGERS_SEED_UPDATED_AT = '2026-10-22T00:00:00.000Z';

// --- Stable charger identifiers -------------------------------------------
// ChargerModel has no explicit id, so a provider's "accepted charge points"
// list stores derived `brand::model` keys. The same key is the registry key
// used for server-side validation. Keeping derive/parse in one place means the
// partner portal, admin drawer, approval diff and email notifications all agree
// on the encoding. `::` never appears in a real brand/model name, and we split
// on the FIRST occurrence so a stray separator in a model name can't corrupt
// the brand half.
const CHARGER_KEY_SEP = '::';

export function chargerKey(brand: string, model: string): string {
  return `${brand.trim()}${CHARGER_KEY_SEP}${model.trim()}`;
}

export function chargerKeyOf(m: Pick<ChargerModel, 'brand' | 'model'>): string {
  return chargerKey(m.brand, m.model);
}

/**
 * No integrated MID meter — the one predicate behind the acceptance rule
 * (2026-09-16). The public signup wizard hard-stops on exactly these models,
 * so a provider may not NEWLY accept one; keys already on their record are
 * exempt and stay editable. Named rather than spelled `status === 'no'` at each
 * site so the picker, the portal and the server answer the same question.
 *
 * Deliberately NOT applied to the wizard's own `depends`/`yes` branches: those
 * ask a different question (what to tell this visitor about their charger).
 */
export const isMidIneligible = (m: Pick<ChargerModel, 'status'>): boolean => m.status === 'no';

export function parseChargerKey(key: string): { brand: string; model: string } {
  const idx = key.indexOf(CHARGER_KEY_SEP);
  if (idx === -1) return { brand: '', model: key };
  return { brand: key.slice(0, idx), model: key.slice(idx + CHARGER_KEY_SEP.length) };
}

// Human label for a stored key, e.g. "Alfen Eve Single S-line". Derived from the
// key itself so it still renders correctly even if the charger was later removed
// from the registry (no registry lookup required).
export function chargerLabel(key: string): string {
  const { brand, model } = parseChargerKey(key);
  return brand ? `${brand} ${model}` : model;
}

// Resolve a typed or picked brand + model to its registry row. Case- and
// whitespace-insensitive on purpose: the free-text fallback of the signup
// wizard is the visitor's own spelling, and "easee home" must meet the same
// MID verdict as the picker's "Easee Home" — the server uses this lookup to
// refuse a lead for a charger the registry knows has no MID meter, and the
// wizard uses it to stop the visitor before they type the rest of the form.
// No match = unknown to us (a typo, or a model we do not list), never a verdict.
function normaliseChargerText(v: string): string {
  return v.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function findChargerModel(
  models: readonly ChargerModel[],
  brand: string,
  model: string
): ChargerModel | undefined {
  const b = normaliseChargerText(brand);
  const m = normaliseChargerText(model);
  if (!b || !m) return undefined;
  return models.find(
    (row) => normaliseChargerText(row.brand) === b && normaliseChargerText(row.model) === m
  );
}
